// BL-177 Step 3: the PURE signature rules, with no imports, so the browser (composer preview) and the Deno Edge Functions
// (the scheduler signs scheduled messages at SEND time) run the very same code. src/lib/signatures.js re-exports everything here
// and adds the phone formatter and the database calls. See signatures.js for the rules themselves.
export function characterCount(text) {
    return Array.from(String(text ?? '')).length;
}
export const PERSONAL_MODES = ['off', 'optional', 'required'];
export const TEXT_CHANNELS = ['sms', 'whatsapp'];
/** No row = the database defaults: company part OFF, empty text, email block on, personal lines optional. */
export function companySettingsFromRow(row) {
    return {
        always_on: row?.always_on === true,
        text_signature: typeof row?.text_signature === 'string' ? row.text_signature : '',
        email_block_enabled: row ? row.email_block_enabled !== false : true,
        personal_signature_mode: PERSONAL_MODES.includes(row?.personal_signature_mode) ? row.personal_signature_mode : 'optional',
    };
}

/** Between the message and the signature: one blank line. */
export const SIGNATURE_SEPARATOR = '\n\n';

export function clean(text) {
    return String(text ?? '').replace(/\r\n?/g, '\n').trim();
}

export function normalizeChannel(channel) {
    const value = String(channel || '').toLowerCase();
    if (value === 'text') return 'sms';
    return value;
}

export function personalMode(companySettings) {
    const mode = companySettings?.personal_signature_mode;
    return PERSONAL_MODES.includes(mode) ? mode : 'optional';
}

/** The email company block as text lines: name, then "phone · website". */
export function emailCompanyLines(block, formatPhone = (value) => String(value ?? '')) {
    if (!block) return [];
    const contact = [formatPhone(block.phone, 'US'), block.website].filter(Boolean).join(' · ');
    return [block.name, contact].filter(Boolean);
}

/**
 * Work out the signature for ONE outgoing message.
 *
 * @param {object} p
 * @param {'sms'|'whatsapp'|'email'} p.channel
 * @param {boolean} [p.isTemplate]          WhatsApp template -> never signed
 * @param {boolean} [p.isAutomatic]         automatic notification / system message -> never signed
 * @param {object}  [p.companySettings]     org_signature_settings row
 *        { always_on, text_signature, email_block_enabled, personal_signature_mode, email_block?: {name, phone, website, logo_url} }
 * @param {object|string} [p.personalSignature]  user_signatures row { text_signature, email_signature } (or the text string)
 * @param {(value: unknown, country?: string) => string} [p.formatPhone]  formats the company phone in the email block (the browser passes the phone helper)
 * @param {boolean} [p.removeMine]          "Remove mine" pressed for this message (ignored when Required)
 * @param {boolean} [p.alreadySignedToday]  legacy input, ignored; every eligible message is signed independently
 * @param {boolean} [p.isFirstReplyOfDay]   legacy input, ignored; every eligible message is signed independently
 * @returns {{ applies, reason, text, personalPart, companyPart, addedChars, canRemoveMine, companyRemovable,
 *             personalRequiredMissing, mode, emailCompanyBlock }}
 */
export function assembleSignature({
    formatPhone,
    channel,
    isTemplate = false,
    isAutomatic = false,
    companySettings = null,
    personalSignature = null,
    removeMine = false,
    alreadySignedToday = false,
    isFirstReplyOfDay = true,
} = {}) {
    const kind = normalizeChannel(channel);
    const isText = TEXT_CHANNELS.includes(kind);
    const mode = personalMode(companySettings);
    const companyOn = companySettings?.always_on === true;
    const none = (reason) => ({
        applies: false, reason, text: '', personalPart: '', companyPart: '', addedChars: 0,
        canRemoveMine: false, companyRemovable: false, personalRequiredMissing: false, mode, emailCompanyBlock: null,
    });

    if (!isText && kind !== 'email') return none('unsupported_channel');
    if (isTemplate) return none('template');
    if (isAutomatic) return none('automatic');
    const personalRaw = typeof personalSignature === 'string'
        ? (isText ? personalSignature : '')
        : (isText ? personalSignature?.text_signature : personalSignature?.email_signature);
    const personalText = clean(personalRaw);
    let personalPart = '';
    if (mode === 'required') personalPart = personalText;
    else if (mode === 'optional' && !removeMine) personalPart = personalText;

    let companyPart = '';
    let emailCompanyBlock = null;
    if (companyOn) {
        if (isText) companyPart = clean(companySettings.text_signature);
        else if (companySettings.email_block_enabled !== false && companySettings.email_block) {
            emailCompanyBlock = companySettings.email_block;
            companyPart = emailCompanyLines(emailCompanyBlock, formatPhone).join('\n');
        }
    }

    const body = [personalPart, companyPart].filter(Boolean).join('\n');
    const text = body ? `${SIGNATURE_SEPARATOR}${body}` : '';
    return {
        applies: Boolean(text),
        reason: text ? 'signed' : 'empty',
        text,
        personalPart,
        companyPart,
        addedChars: characterCount(text),
        canRemoveMine: mode === 'optional' && Boolean(personalText),
        companyRemovable: false,
        personalRequiredMissing: mode === 'required' && !personalText,
        mode,
        emailCompanyBlock,
    };
}

/** Message body + signature (trailing spaces/blank lines on the body are trimmed first so the gap is always one line). */
export function appendSignature(body, signature) {
    const sig = typeof signature === 'string' ? signature : signature?.text || '';
    const text = String(body ?? '');
    if (!sig) return text;
    const message = text.replace(/\s+$/g, '');
    return message ? `${message}${sig}` : sig.replace(/^\n+/, '');
}
